import type { ReactNode } from 'react';
import { TrendingDown, TrendingUp } from 'lucide-react';
import { cn } from '@/lib/cn';

export interface OverviewStat {
  id: string;
  label: string;
  value: ReactNode;
  icon?: ReactNode;
  hint?: ReactNode;
  /** Optional change from previous period. */
  delta?: {
    value: number;
    label?: string;
    direction?: 'up' | 'down';
  };
}

export interface OverviewPanelProps {
  stats: OverviewStat[];
  title?: ReactNode;
  className?: string;
}

/** Complete class map so Tailwind keeps the utilities (dynamic `lg:grid-cols-${n}` is purged). */
const lgGridCols: Record<number, string> = {
  1: 'lg:grid-cols-1',
  2: 'lg:grid-cols-2',
  3: 'lg:grid-cols-3',
  4: 'lg:grid-cols-4',
  5: 'lg:grid-cols-5',
  6: 'lg:grid-cols-6',
};

/**
 * Dashboard summary strip shown above grids. Mirrors TailAdmin's
 * invoices-overview panel: each stat has a label, a large value, an optional
 * icon and an optional delta tag.
 */
export function OverviewPanel({
  stats,
  title,
  className,
}: OverviewPanelProps) {
  return (
    <section
      className={cn(
        'rounded-2xl border border-gray-200 bg-white shadow-card dark:border-white/10 dark:bg-gray-900 dark:shadow-card-dark',
        className,
      )}
    >
      {title !== undefined && (
        <header className="border-b border-gray-200 px-5 py-4 dark:border-white/10">
          <h2 className="text-sm font-semibold text-gray-900 dark:text-white">
            {title}
          </h2>
        </header>
      )}
      <div
        className={cn(
          'grid grid-cols-1 gap-px bg-gray-200 sm:grid-cols-2 dark:bg-white/10',
          lgGridCols[stats.length] ?? 'lg:grid-cols-4',
        )}
      >
        {stats.map((stat) => (
          <StatCell key={stat.id} stat={stat} />
        ))}
      </div>
    </section>
  );
}

function StatCell({ stat }: { stat: OverviewStat }) {
  const deltaDirection =
    stat.delta?.direction ??
    (stat.delta && stat.delta.value >= 0 ? 'up' : 'down');

  return (
    <div
      className="flex flex-col gap-3 bg-white p-5 dark:bg-gray-900"
      data-stat-id={stat.id}
    >
      <div className="flex items-center justify-between gap-2">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
          {stat.label}
        </span>
        {stat.icon && (
          <span className="flex h-9 w-9 items-center justify-center rounded-lg bg-brand-50 text-brand-600 dark:bg-brand-900/30 dark:text-brand-300">
            {stat.icon}
          </span>
        )}
      </div>
      <div className="flex items-baseline gap-2">
        <span className="text-2xl font-semibold text-gray-900 dark:text-white">
          {stat.value}
        </span>
        {stat.delta && (
          <span
            className={cn(
              'inline-flex items-center gap-0.5 rounded-full px-1.5 py-0.5 text-[11px] font-medium',
              deltaDirection === 'up'
                ? 'bg-emerald-50 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300'
                : 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
            )}
          >
            {deltaDirection === 'up' ? (
              <TrendingUp className="h-3 w-3" aria-hidden="true" />
            ) : (
              <TrendingDown className="h-3 w-3" aria-hidden="true" />
            )}
            {stat.delta.label ??
              `${stat.delta.value >= 0 ? '+' : ''}${stat.delta.value.toFixed(1)}%`}
          </span>
        )}
      </div>
      {stat.hint && (
        <div className="text-xs text-gray-500 dark:text-gray-400">
          {stat.hint}
        </div>
      )}
    </div>
  );
}
